import React from "react";
import Typography from "components/typography";
import type { HistoryDataSidebarItem } from "./history-data-sidebar";

interface HistoryDataSidebarTimelineItemProps {
  item: HistoryDataSidebarItem;
  isLast: boolean;
}

const HistoryDataSidebarTimelineItem: React.FC<HistoryDataSidebarTimelineItemProps> = ({
  item,
  isLast,
}) => {
  const actionText = item.actionLabel || "Edit";

  return (
    <div className="relative flex gap-4 pl-6">
      {!isLast && (
        <span className="absolute left-[5px] top-5 h-[calc(100%-12px)] w-px bg-neutral-200" />
      )}
      <span className="absolute left-0 top-2 h-3 w-3 rounded-full border-[3px] border-primary-100 bg-primary-600" />

      <div className="flex-1 pb-6">
        {/* Timestamp */}
        <Typography variant="bodySmall" tone="muted" className="mb-1 text-[11px]">
          {item.timestampLabel}
        </Typography>

        {/* Actor Name & Action Label */}
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="font-semibold text-neutral-900 text-sm">{item.actorName}</span>
          <span className="text-neutral-400">•</span>
          <span className="font-medium text-neutral-500 text-sm">{actionText}</span>
        </div>

        {/* Field Changes Bullet Points */}
        {item.fieldChanges && item.fieldChanges.length > 0 && (
          <ul className="list-disc pl-4 space-y-1.5 text-xs text-neutral-600 my-2">
            {item.fieldChanges.map((change, idx) => (
              <li key={idx} className="leading-relaxed">
                {change.sentence || (
                  <span>
                    <span className="font-semibold text-neutral-800">{change.fieldLabel}: </span>
                    {change.type === "add" && <span className="text-green-700">Add "{change.newValue}"</span>}
                    {change.type === "delete" && <span className="text-red-700">Delete "{change.oldValue}"</span>}
                    {change.type === "change" && (
                      <span>
                        <span className="line-through text-neutral-400 mr-1">{change.oldValue ?? "—"}</span>
                        <span>&rarr;</span>
                        <span className="font-semibold text-neutral-800 ml-1">{change.newValue ?? "—"}</span>
                      </span>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Custom Description Fallback */}
        {(!item.fieldChanges || item.fieldChanges.length === 0) && item.description && (
          <div className="mt-1.5 text-xs text-neutral-600">
            {typeof item.description === "string" ? (
              <Typography variant="bodySmall" tone="secondary">
                {item.description}
              </Typography>
            ) : (
              item.description
            )}
          </div>
        )}

        {/* Reason Callout Block */}
        {item.reason && (
          <div className="mt-2.5 rounded-xl border border-neutral-200/80 bg-neutral-50/90 p-3 text-xs text-neutral-800 shadow-2xs">
            <span className="font-semibold text-neutral-900 block mb-1">Reasons:</span>
            <span className="text-neutral-600 leading-relaxed">{item.reason}</span>
          </div>
        )}

        {/* Custom Detail Fallback */}
        {item.detail && (
          <div className="mt-2">
            {typeof item.detail === "string" ? (
              <Typography variant="bodySmall" tone="muted">
                {item.detail}
              </Typography>
            ) : (
              item.detail
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryDataSidebarTimelineItem;